import {
  Box,
  Chip,
  Pagination,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertCircle, ArrowDownLeft, ArrowUpRight, Check } from "lucide-react";
import React, { useMemo, useState, useCallback } from "react";
import { EmailCard } from "./components/EmailCard";
import { useEmailMatching } from "./hooks/useEmailMatching";
import { EmailMessage, EnhancedEmailMessage, ProcessedEmail } from "./types";
import { LoadingOverlay } from "./components/LoadingOverlay";
import { useGetMyCustomersQuery } from "../../store/services/customerService";

type FilterType = "matched" | "unmatched";
type TabType = "incoming" | "outgoing" | "internal" | "all";

// Interface för användarinformation
interface UserInfo {
  mail: string;
  userPrincipalName: string;
  displayName: string;
}

interface EmailMatcherProps {
  emails: ProcessedEmail[];
  folderMap?: Record<string, string>;
  userInfo?: UserInfo | null;
}

export const EmailMatcher: React.FC<EmailMatcherProps> = ({
  emails,
  folderMap = {},
  userInfo = null,
}) => {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<FilterType>("matched");
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const itemsPerPage = isMobile ? 1 : 4;

  const { data: customerData } = useGetMyCustomersQuery({});
  const { emailMatches, confirmMatch, isLoading } = useEmailMatching(
    emails,
    customerData || []
  );

  // Funktion för att kontrollera om en e-postadress tillhör användaren/organisationen
  const isUserOrOrgEmail = (emailAddress?: string | null): boolean => {
    if (!userInfo) return false; // Om ingen användarinfo finns, anta att det inte är användaren
    if (!emailAddress) return false; // Om ingen e-postadress finns, anta att det inte är användaren
    // Kontrollera om adressen är användarens
    if (
      userInfo.mail &&
      emailAddress.toLowerCase() === userInfo.mail.toLowerCase()
    ) {
      return true;
    }

    if (
      userInfo.userPrincipalName &&
      emailAddress.toLowerCase() === userInfo.userPrincipalName.toLowerCase()
    ) {
      return true;
    }

    // Kontrollera om domänen matchar användarens domän
    if (userInfo.mail && userInfo.mail.includes("@")) {
      const domain = userInfo.mail.split("@")[1].toLowerCase();
      if (emailAddress.toLowerCase().endsWith("@" + domain)) {
        return true;
      }
    }

    // Kontrollera alternativdomän från userPrincipalName om den är annorlunda
    if (
      userInfo.userPrincipalName &&
      userInfo.userPrincipalName.includes("@")
    ) {
      const upnDomain = userInfo.userPrincipalName.split("@")[1].toLowerCase();
      if (userInfo.mail) {
        const mailDomain = userInfo.mail.split("@")[1].toLowerCase();
        // Bara kolla UPN-domänen om den skiljer sig från mail-domänen
        if (
          upnDomain !== mailDomain &&
          emailAddress.toLowerCase().endsWith("@" + upnDomain)
        ) {
          return true;
        }
      } else if (emailAddress.toLowerCase().endsWith("@" + upnDomain)) {
        return true;
      }
    }

    return false;
  };

  // Funktion för att avgöra typ av e-post baserat på mappinformation och andra data
  const determineEmailType = useCallback((email: EmailMessage): TabType => {
    // Kontrollera om förbättrad e-post med parentFolderId
    const enhancedEmail = email as EnhancedEmailMessage;
    const folderId = enhancedEmail.parentFolderId;

    if (folderId && folderMap[folderId]) {
      const folderName = folderMap[folderId].toLowerCase();

      // Om e-postet finns i skickat-mappen är det utgående
      if (folderName === "sent items" || folderName === "skickat") {
        return "outgoing";
      }
      // Om e-postet finns i inkorgen, dubbelkolla för att se om det är internt
      else if (folderName === "inbox" || folderName === "inkorg") {
        // Om avsändaren är från vår organisation, är det internt
        if (email.from && isUserOrOrgEmail(email.from.emailAddress.address)) {
          return "internal";
        }
        return "incoming";
      }
    }

    // Fallback till vår tidigare logik baserat på avsändare/mottagare
    const isToOurOrg =
      email.toRecipients &&
      email.toRecipients.some((recipient) =>
        isUserOrOrgEmail(recipient.emailAddress.address)
      );

    const isFromOurOrg =
      email.from && isUserOrOrgEmail(email.from.emailAddress.address);

    if (isFromOurOrg && isToOurOrg) {
      return "internal";
    } else if (isFromOurOrg) {
      return "outgoing";
    } else if (isToOurOrg) {
      return "incoming";
    }

    // Om vi inte kan avgöra, anta att det är inkommande
    return "incoming";
  }, [folderMap, userInfo]);
  console.log(emailMatches);
  // Paketera information om e-posttyp med varje e-post
  const enhancedEmailMatches = useMemo(() => {
    return emailMatches.map((email) => ({
      ...email,
      emailType: determineEmailType(email),

    }));
  }, [emailMatches, determineEmailType]);

  // Separate emails by direction (incoming/outgoing/internal)
  const categorizedEmails = useMemo(() => {
    const incoming = enhancedEmailMatches.filter(
      (email) => email.emailType === "incoming"
    );
    const outgoing = enhancedEmailMatches.filter(
      (email) => email.emailType === "outgoing"
    );
    const internal = enhancedEmailMatches.filter(
      (email) => email.emailType === "internal"
    );

    return {
      all: enhancedEmailMatches,
      incoming,
      outgoing,
      internal,
    };
  }, [enhancedEmailMatches]);

  // Filter emails based on selected filter and tab
  const filteredEmails = useMemo(() => {
    const emailsForTab = categorizedEmails[activeTab];

    switch (filter) {
      case "matched":
        return emailsForTab.filter((email) => email.matches && email.matches.length > 0);
      case "unmatched":
        return emailsForTab.filter((email) => email.matches && email.matches.length === 0);
      default:
        return emailsForTab;
    }
  }, [categorizedEmails, activeTab, filter]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: FilterType
  ) => {
    if (newFilter !== null) {
      setFilter(newFilter);
      setPage(1); // Reset to first page when filter changes
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newTab: TabType) => {
    setActiveTab(newTab);
    setPage(1); // Reset to first page when tab changes
  };

  const currentEmails = filteredEmails.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Calculate statistics
  const stats = useMemo(
    () => ({
      all: {
        total: enhancedEmailMatches.length,
        matched: enhancedEmailMatches.filter((e) => e.matches && e.matches.length > 0)
          .length,
        unmatched: enhancedEmailMatches.filter((e) => e.matches && e.matches.length === 0)
          .length,
      },
      incoming: {
        total: categorizedEmails.incoming.length,
        matched: categorizedEmails.incoming.filter((e) => e.matches && e.matches.length > 0)
          .length,
        unmatched: categorizedEmails.incoming.filter(
          (e) => e.matches && e.matches.length === 0
        ).length,
      },
      outgoing: {
        total: categorizedEmails.outgoing.length,
        matched: categorizedEmails.outgoing.filter((e) => e.matches && e.matches.length > 0)
          .length,
        unmatched: categorizedEmails.outgoing.filter(
          (e) => e.matches && e.matches.length === 0
        ).length,
      },
      internal: {
        total: categorizedEmails.internal.length,
        matched: categorizedEmails.internal.filter((e) => e.matches.length > 0)
          .length,
        unmatched: categorizedEmails.internal.filter(
          (e) => e.matches && e.matches.length === 0
        ).length,
      },
    }),
    [enhancedEmailMatches, categorizedEmails]
  );

  return (
    <Stack sx={{ position: "relative" }}>
      <LoadingOverlay show={isLoading} />
      <Stack spacing={3}>
        <Box sx={{ px: 2 }}>
          <Typography variant="h6" gutterBottom>
            Synka e-postmeddelanden
          </Typography>

          {/* Tabs for Email Direction */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="email direction tabs"
              variant={isMobile ? "fullWidth" : "standard"}
            >
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>Alla</span>
                    <Chip size="small" label={stats.all.matched} />
                  </Box>
                }
                value="all"
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ArrowDownLeft size={16} />
                    <span>Inkommande</span>
                    <Chip size="small" label={stats.incoming.matched} />
                  </Box>
                }
                value="incoming"
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ArrowUpRight size={16} />
                    <span>Utgående</span>
                    <Chip size="small" label={stats.outgoing.matched} />
                  </Box>
                }
                value="outgoing"
              />
              {/* <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h1" />
                      <path d="M16 3h1a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-1" />
                      <path d="M12 22v-8" />
                      <path d="M12 14 9 11" />
                      <path d="m12 14 3-3" />
                      <path d="M8 7h8" />
                    </svg>
                    <span>Internt</span>
                    <Chip size="small" label={stats.internal.total} />
                  </Box>
                }
                value="internal"
              /> */}
            </Tabs>
          </Box>

          {/* Filter and Stats */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 3 }}
          >
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={handleFilterChange}
              aria-label="email filter"
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              <ToggleButton value="matched">
                <Check size={16} />
                <Box sx={{ ml: 1 }}>Matchade ({stats[activeTab].matched})</Box>
              </ToggleButton>
              {activeTab === "all" && (
                <ToggleButton value="unmatched">
                  <AlertCircle size={16} />
                  <Box sx={{ ml: 1 }}>
                    Ej matchade ({stats[activeTab].unmatched})
                  </Box>
                </ToggleButton>
              )}
            </ToggleButtonGroup>

            {/* Current filter indicator for mobile */}
            {isMobile && (
              <Chip
                label={`Visar: ${
                  filter === "matched" ? "Matchade" : "Ej matchade"
                } emails (${
                  activeTab === "all"
                    ? "Alla"
                    : activeTab === "incoming"
                    ? "Inkommande"
                    : activeTab === "outgoing"
                    ? "Utgående"
                    : "Internt"
                })`}
                size="small"
              />
            )}
          </Stack>

          {/* Email grid */}
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(auto-fill, minmax(350px, 1fr))",
              },
            }}
          >
            {currentEmails.map((email) => (
              <EmailCard
                key={email.id}
                email={email}
                emailType={email.emailType}
                onConfirmMatch={confirmMatch}
              />
            ))}
          </Box>

          {/* Show message if no emails match filter */}
          {currentEmails.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "text.secondary",
              }}
            >
              <Typography variant="body1">
                Inga emails matchar valt filter
              </Typography>
            </Box>
          )}
        </Box>

        {/* Pagination */}
        {filteredEmails.length > itemsPerPage && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              pb: 2,
            }}
          >
            <Pagination
              count={Math.ceil(filteredEmails.length / itemsPerPage)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default EmailMatcher;
